import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useAuth from '../../hooks/useAuth';
import { supportWidgetService, supportInboxService } from '../../services/supportWidgetService';
import { isPushSupported, getPushPermission, subscribeToPush } from '../../services/pushService';
import api from '../../services/api';
import './SupportWidget.css';

const OWNER_EMAIL = 'demetrion@fitness1stsports.com';
const TYPES = ['Bug', 'Feature', 'Improvement', 'Question'];
const PRIORITIES = ['Low', 'Medium', 'High'];
const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const MAX_IMAGES = 5;
const UPLOADS_ORIGIN = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');

const seenKey = (userId, mode) => `hub_support_widget_seen_${mode}_${userId}`;

const loadSeenMap = (userId, mode) => {
  try {
    const raw = localStorage.getItem(seenKey(userId, mode));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const saveSeenMap = (userId, mode, map) => {
  try {
    localStorage.setItem(seenKey(userId, mode), JSON.stringify(map));
  } catch (error) {
    // localStorage unavailable (private mode, etc.) - unread badge just won't persist
  }
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
};

const formatTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const attachmentUrl = (path) => `${UPLOADS_ORIGIN}${path}`;

const isImageFile = (file) => file.type.startsWith('image/');

// Floating chat widget, visible to every logged-in staff member on every HUB page. For
// everyone else it's "reach Demetrion" -- their own requests, scoped by submitted_by. For
// Demetrion himself it's an inbox -- every conversation anyone has started, with a reply
// box and a status control, because he's the recipient, not someone filing his own tickets.
// Both modes ride the same support_tickets/support_ticket_comments tables and the same SSE
// channel; only the backend scoping (and this component's view) differs.
const SupportWidget = () => {
  const { user } = useAuth();
  const isOwner = user?.email === OWNER_EMAIL;
  const service = isOwner ? supportInboxService : supportWidgetService;
  const seenMode = isOwner ? 'inbox' : 'tickets';

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | new | thread
  const [tickets, setTickets] = useState([]);
  const [seenMap, setSeenMap] = useState({});
  const [selected, setSelected] = useState(null); // { ticket, comments }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyImages, setReplyImages] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newType, setNewType] = useState('Question');
  const [newPriority, setNewPriority] = useState('Medium');
  const [newImages, setNewImages] = useState([]);
  const [error, setError] = useState('');
  const [pushPromptVisible, setPushPromptVisible] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [remoteTyping, setRemoteTyping] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const selectedIdRef = useRef(null);
  const threadEndRef = useRef(null);
  const replyInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    if (user?.id) setSeenMap(loadSeenMap(user.id, seenMode));
  }, [user?.id, seenMode]);

  useEffect(() => {
    selectedIdRef.current = selected?.ticket?.id || null;
  }, [selected]);

  useEffect(() => {
    if (isOwner && isPushSupported() && getPushPermission() === 'default') {
      setPushPromptVisible(true);
    }
  }, [isOwner]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [selected?.comments?.length, view]);

  const refreshTickets = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await service.getAll();
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('Error fetching support widget tickets:', error);
    }
  }, [user?.id, service]);

  const refreshSelectedThread = useCallback(async (ticketId) => {
    try {
      const data = await service.getOne(ticketId);
      setSelected((prev) => (prev?.ticket?.id === ticketId ? data : prev));
    } catch (error) {
      console.error('Error refreshing support widget thread:', error);
    }
  }, [service]);

  useEffect(() => {
    if (!user?.id) return undefined;
    refreshTickets();

    const source = new EventSource(`${api.defaults.baseURL}/support-widget/stream`, { withCredentials: true });
    source.addEventListener('support-widget-message', (event) => {
      refreshTickets();
      try {
        const { ticketId } = JSON.parse(event.data);
        if (ticketId === selectedIdRef.current) refreshSelectedThread(ticketId);
      } catch (error) {
        console.error('Error parsing support widget realtime event:', error);
      }
    });
    source.addEventListener('support-widget-typing', (event) => {
      try {
        const { ticketId } = JSON.parse(event.data);
        if (ticketId !== selectedIdRef.current) return;
        setRemoteTyping(true);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setRemoteTyping(false), 4000);
      } catch (error) {
        console.error('Error parsing support widget typing event:', error);
      }
    });
    source.onerror = () => {
      // EventSource reconnects on its own.
    };

    const interval = setInterval(refreshTickets, 120000);
    return () => {
      source.close();
      clearInterval(interval);
      clearTimeout(typingTimeoutRef.current);
    };
  }, [user?.id, refreshTickets, refreshSelectedThread]);

  const unreadCount = useMemo(() => tickets.reduce((sum, t) => {
    const seen = seenMap[t.id] || 0;
    const total = Number(t.comment_count) || 0;
    return sum + Math.max(0, total - seen);
  }, 0), [tickets, seenMap]);

  // App icon badge (Home Screen PWA) -- the definitive sync point. The service worker
  // attempts this too on each push so the badge can update while the app is closed, but
  // browser/OS support for that varies; this foreground effect is guaranteed to run the
  // moment the app is opened, so the badge is never stuck wrong for long.
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (unreadCount > 0) {
      navigator.setAppBadge(unreadCount).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }, [unreadCount]);

  if (!user) return null;

  const enablePush = async () => {
    setPushPromptVisible(false);
    try {
      await subscribeToPush();
    } catch (error) {
      console.error('Error enabling push notifications:', error);
    }
  };

  const markSeen = (ticketId, count) => {
    const next = { ...seenMap, [ticketId]: count };
    setSeenMap(next);
    saveSeenMap(user.id, seenMode, next);
  };

  const openWidget = () => {
    setOpen(true);
    setView('list');
    setError('');
    refreshTickets();
  };

  const openThread = async (ticketId) => {
    setLoading(true);
    setError('');
    setRemoteTyping(false);
    try {
      const data = await service.getOne(ticketId);
      setSelected(data);
      setView('thread');
      markSeen(ticketId, data.comments.length);
    } catch (error) {
      console.error('Error loading support request:', error);
      setError('Could not load that request.');
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setNewSubject('');
    setNewMessage('');
    setNewType('Question');
    setNewPriority('Medium');
    setNewImages([]);
    setError('');
    setView('new');
  };

  const addImages = (fileList, setter, current) => {
    const files = Array.from(fileList).filter(isImageFile);
    if (files.length === 0) return;
    setter([...current, ...files].slice(0, MAX_IMAGES));
  };

  const notifyTyping = (ticketId) => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 3000) return;
    lastTypingSentRef.current = now;
    service.sendTyping(ticketId);
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if ((!newSubject.trim() || (!newMessage.trim() && newImages.length === 0)) || sending) return;
    setSending(true);
    setError('');
    try {
      const data = await supportWidgetService.createTicket({
        subject: newSubject.trim(),
        description: newMessage.trim(),
        ticket_type: newType,
        priority: newPriority,
        images: newImages,
      });
      markSeen(data.ticket.id, 0);
      await refreshTickets();
      await openThread(data.ticket.id);
    } catch (error) {
      console.error('Error creating support request:', error);
      setError('Could not send that. Try again.');
    } finally {
      setSending(false);
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if ((!replyText.trim() && replyImages.length === 0) || !selected || sending) return;
    setSending(true);
    setError('');
    try {
      const data = await service.addComment(selected.ticket.id, replyText.trim(), replyImages);
      const updatedComments = [...selected.comments, data.comment];
      setSelected({ ...selected, comments: updatedComments });
      markSeen(selected.ticket.id, updatedComments.length);
      setReplyText('');
      setReplyImages([]);
      refreshTickets();
    } catch (error) {
      console.error('Error sending support widget reply:', error);
      setError('Could not send that. Try again.');
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    if (!selected || updatingStatus || status === selected.ticket.status) return;
    setUpdatingStatus(true);
    try {
      const data = await supportInboxService.updateStatus(selected.ticket.id, status);
      setSelected({ ...selected, ticket: { ...selected.ticket, status: data.ticket.status } });
      refreshTickets();
    } catch (error) {
      console.error('Error updating support widget status:', error);
      setError('Could not update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (view === 'thread') addImages(e.dataTransfer.files, setReplyImages, replyImages);
    else if (view === 'new') addImages(e.dataTransfer.files, setNewImages, newImages);
  };

  const handlePaste = (e, setter, current) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter((item) => item.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    const files = imageItems.map((item) => item.getAsFile()).filter(Boolean);
    setter([...current, ...files].slice(0, MAX_IMAGES));
  };

  const renderImagePreviews = (images, setter) => {
    if (images.length === 0) return null;
    return (
      <div className="support-widget-preview-strip">
        {images.map((file, i) => (
          <div key={i} className="support-widget-preview-thumb">
            <img src={URL.createObjectURL(file)} alt={file.name} />
            <button type="button" className="support-widget-preview-remove" onClick={() => setter(images.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        ))}
      </div>
    );
  };

  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;
    return (
      <div className="support-widget-attachments">
        {attachments.map((a) => (
          <img
            key={a.id}
            src={attachmentUrl(a.file_path)}
            alt={a.original_filename}
            className="support-widget-attachment-thumb"
            onClick={() => setLightbox(attachmentUrl(a.file_path))}
          />
        ))}
      </div>
    );
  };

  const otherPartyName = isOwner ? (selected?.ticket?.submitted_by_name || 'Them') : 'Demetrion';
  const rootIsMine = selected ? selected.ticket.submitted_by === user.id : false;

  return (
    <div
      className="support-widget"
      onDragOver={(e) => { e.preventDefault(); if (view === 'thread' || view === 'new') setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
    >
      {lightbox && (
        <div className="support-widget-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
        </div>
      )}

      {open && (
        <div className={`support-widget-panel ${dragActive ? 'drag-active' : ''}`}>
          {dragActive && <div className="support-widget-drop-overlay">Drop image to attach</div>}

          <div className="support-widget-header">
            {view !== 'list' ? (
              <button type="button" className="support-widget-back" onClick={() => setView('list')} aria-label="Back">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
            ) : (
              <div className="support-widget-header-avatar">
                {isOwner ? initials(`${user.firstName || ''} ${user.lastName || ''}`) : 'D'}
              </div>
            )}
            <div className="support-widget-header-text">
              <span className="support-widget-title">
                {view === 'list' && (isOwner ? 'Inbox' : 'Support')}
                {view === 'new' && 'New Request'}
                {view === 'thread' && (isOwner ? otherPartyName : (selected?.ticket.subject || ''))}
              </span>
              <span className="support-widget-subtitle">
                {view === 'list' && (isOwner ? 'Everything sent to you' : 'Reach Demetrion directly')}
                {view === 'new' && 'What do you need?'}
                {view === 'thread' && (isOwner ? selected?.ticket.subject : selected?.ticket.ticket_number)}
              </span>
            </div>
            <button type="button" className="support-widget-close" onClick={() => setOpen(false)} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {view === 'thread' && isOwner && selected && (
            <div className="support-widget-status-row">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`support-widget-status-chip status-${s.replace(/\s/g, '-').toLowerCase()} ${selected.ticket.status === s ? 'active' : ''}`}
                  onClick={() => changeStatus(s)}
                  disabled={updatingStatus}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {error && <div className="support-widget-error">{error}</div>}

          {loading && (
            <div className="support-widget-body">
              <div className="support-widget-skeleton" />
              <div className="support-widget-skeleton" />
              <div className="support-widget-skeleton short" />
            </div>
          )}

          {view === 'list' && !loading && (
            <div className="support-widget-body">
              {pushPromptVisible && (
                <div className="support-widget-push-prompt">
                  <span>Get notified here the instant someone messages you.</span>
                  <div className="support-widget-push-actions">
                    <button type="button" className="support-widget-push-enable" onClick={enablePush}>Enable</button>
                    <button type="button" className="support-widget-push-dismiss" onClick={() => setPushPromptVisible(false)}>Not now</button>
                  </div>
                </div>
              )}
              {!isOwner && (
                <button type="button" className="support-widget-new-btn" onClick={startNew}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  New Request
                </button>
              )}
              {tickets.length === 0 ? (
                <div className="support-widget-empty">
                  <div className="support-widget-empty-icon">💬</div>
                  <p>{isOwner ? <>Nothing yet.<br />Messages from your team will show up here.</> : <>No requests yet.<br />Send Demetrion a message about anything you need on HUB.</>}</p>
                </div>
              ) : (
                <div className="support-widget-list">
                  {tickets.map((t) => {
                    const unread = Math.max(0, (Number(t.comment_count) || 0) - (seenMap[t.id] || 0));
                    return (
                      <div key={t.id} className="support-widget-item" onClick={() => openThread(t.id)}>
                        <div className="support-widget-item-top">
                          <span className={`support-widget-status status-${t.status.replace(/\s/g, '-').toLowerCase()}`}>{t.status}</span>
                          <span className="support-widget-item-time">{formatTime(t.updated_at || t.created_at)}</span>
                        </div>
                        {isOwner && <span className="support-widget-item-sender">{t.submitted_by_name || 'Someone'}</span>}
                        <div className="support-widget-item-subject">
                          {t.subject}
                          {unread > 0 && <span className="support-widget-unread-dot" />}
                        </div>
                        <div className="support-widget-item-meta">{t.ticket_number}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {view === 'new' && !loading && !isOwner && (
            <form className="support-widget-body" onSubmit={submitNew}>
              <label className="support-widget-field-label">Type</label>
              <div className="support-widget-type-chips">
                {TYPES.map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`support-widget-chip ${newType === t ? 'active' : ''}`}
                    onClick={() => setNewType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <label className="support-widget-field-label">Priority</label>
              <div className="support-widget-type-chips">
                {PRIORITIES.map((p) => (
                  <button
                    type="button"
                    key={p}
                    className={`support-widget-chip priority-chip priority-${p.toLowerCase()} ${newPriority === p ? 'active' : ''}`}
                    onClick={() => setNewPriority(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <input
                className="support-widget-input"
                placeholder="What's this about?"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                maxLength={255}
                required
              />
              <textarea
                className="support-widget-textarea"
                placeholder="Tell Demetrion what you need... (you can paste or drop an image too)"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onPaste={(e) => handlePaste(e, setNewImages, newImages)}
              />
              {renderImagePreviews(newImages, setNewImages)}

              <div className="support-widget-composer-row">
                <label className="support-widget-attach-btn" title="Attach image">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => addImages(e.target.files, setNewImages, newImages)} />
                </label>
                <button className="support-widget-send-btn" type="submit" disabled={sending}>
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          )}

          {view === 'thread' && selected && !loading && (
            <>
              <div className="support-widget-thread">
                <div className={`support-widget-message ${rootIsMine ? 'support-widget-message-mine' : 'support-widget-message-theirs'}`}>
                  {!rootIsMine && <div className="support-widget-avatar">{initials(otherPartyName)}</div>}
                  <div className="support-widget-message-col">
                    <div className="support-widget-message-bubble">
                      {selected.ticket.description && selected.ticket.description !== '📷 Image attached' && (
                        <div className="support-widget-message-text">{selected.ticket.description}</div>
                      )}
                      {renderAttachments(selected.ticket.attachments)}
                    </div>
                    <div className="support-widget-message-time">{formatTime(selected.ticket.created_at)}</div>
                  </div>
                </div>
                {selected.comments.map((c) => {
                  const mine = c.created_by === user.id;
                  return (
                    <div key={c.id} className={`support-widget-message ${mine ? 'support-widget-message-mine' : 'support-widget-message-theirs'}`}>
                      {!mine && <div className="support-widget-avatar">{initials(c.created_by_name || otherPartyName)}</div>}
                      <div className="support-widget-message-col">
                        <div className="support-widget-message-bubble">
                          {c.comment && c.comment !== '📷 Image' && (
                            <div className="support-widget-message-text">{c.comment}</div>
                          )}
                          {renderAttachments(c.attachments)}
                        </div>
                        <div className="support-widget-message-time">{formatTime(c.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                {remoteTyping && (
                  <div className="support-widget-message support-widget-message-theirs">
                    <div className="support-widget-avatar">{initials(otherPartyName)}</div>
                    <div className="support-widget-typing-bubble">
                      <span /><span /><span />
                    </div>
                  </div>
                )}
                <div ref={threadEndRef} />
              </div>
              {renderImagePreviews(replyImages, setReplyImages)}
              <form className="support-widget-composer" onSubmit={submitReply}>
                <label className="support-widget-attach-btn" title="Attach image">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => addImages(e.target.files, setReplyImages, replyImages)} />
                </label>
                <input
                  ref={replyInputRef}
                  className="support-widget-input"
                  placeholder="Type a message..."
                  value={replyText}
                  onChange={(e) => { setReplyText(e.target.value); notifyTyping(selected.ticket.id); }}
                  onPaste={(e) => handlePaste(e, setReplyImages, replyImages)}
                  disabled={sending}
                />
                <button className="support-widget-send-btn" type="submit" disabled={sending || (!replyText.trim() && replyImages.length === 0)}>
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="support-widget-bubble"
        onClick={() => (open ? setOpen(false) : openWidget())}
        title={isOwner ? 'Inbox' : 'Support'}
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
        )}
        {!open && unreadCount > 0 && <span className="support-widget-badge">{unreadCount}</span>}
      </button>
    </div>
  );
};

export default SupportWidget;
